import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";

const FALLBACK = [
  { q: "What makes Morning Soft different?", a: "Morning Soft is built around one simple idea. Every day should begin with comfort." },
];

function FaqItem({ item, index, open, onToggle }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.04 }}
      className={`overflow-hidden rounded-2xl border transition-colors ${open ? "border-[#173a7a] bg-white shadow-[0_20px_50px_-25px_rgba(23,58,122,0.4)]" : "border-slate-200 bg-white"}`}
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={onToggle}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-7 py-5"
        aria-expanded={open}
      >
        <span className="flex items-start gap-4">
          <span className="mt-0.5 shrink-0 w-8 h-8 rounded-full bg-sky-50 text-[#173a7a] flex items-center justify-center font-display font-bold text-sm">
            {index + 1}
          </span>
          <span className={`font-display font-bold text-base sm:text-lg leading-snug transition-colors ${open ? "text-[#0f2a5c]" : "text-slate-800"}`}>
            {item.q}
          </span>
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3 }}
          className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${open ? "bg-[#173a7a] text-white" : "bg-slate-100 text-slate-500"}`}
        >
          <Plus size={16} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="px-5 sm:px-7 pb-6 pl-16 sm:pl-[76px] text-sm sm:text-base text-slate-600 leading-relaxed">
              {item.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function Faq() {
  const { content } = useContent();
  const FAQS = content?.faq?.length ? content.faq : FALLBACK;
  const [open, setOpen] = useState(0);

  return (
    <section className="relative bg-gradient-to-b from-white via-sky-50/30 to-white border-y border-slate-100" data-testid="faq-section" id="faq">
      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-sky-100 shadow-sm">
            <HelpCircle size={14} className="text-amber-500" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#173a7a]">Questions & Answers</span>
          </div>
          <h2 className="mt-5 font-display text-3xl md:text-5xl font-black text-[#0f2a5c] tracking-tight leading-[1.04]">
            Everything you wanted to know.
          </h2>
          <p className="mt-4 text-slate-600 max-w-xl mx-auto">
            Softness, strength, shipping and wholesale — the seven questions we hear most often.
          </p>
        </div>

        <div className="mt-12 grid gap-4">
          {FAQS.map((f, i) => (
            <FaqItem
              key={f.q}
              item={f}
              index={i}
              open={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-14 rounded-3xl bg-[#0f2a5c] text-white p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-bold">Still curious?</p>
            <p className="mt-2 font-display text-2xl md:text-3xl font-black leading-tight">
              We&apos;re a message away.
            </p>
            <p className="mt-1 text-sky-100/80 text-sm">
              Retail, wholesale, press or a simple hello — we reply within one business day.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="faq-contact-btn"
            className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#0f2a5c] font-display font-bold px-7 py-3.5 text-sm uppercase tracking-widest hover:bg-white transition-colors whitespace-nowrap"
          >
            Contact Morning Soft
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
